import { useEffect, useState } from "react";
import { ChevronDown, ChevronUp, Info } from "lucide-react";
import "./style.scss";
import CheckboxWrapper from "../../../../atoms/CheckBoxWrapper/Checkbox"; 
import CustomButton from "../../../../atoms/CustomButton";
import { useSelector } from "react-redux";
import { getData, postData } from "../../../../../services";
import { useCookies } from "react-cookie";
import toast from "react-hot-toast";
import { confimationStyles } from "../../../../../assets/styles/toast";

const NotificationPermissions = () => {
  const [cookies] = useCookies(["e_t"]);
  const accessData = useSelector((state) => state.accessDetails?.accessData);
  const [notificationData, setNotificationData] = useState([]);
  const isAdmin = accessData?.role?.label === "Admin";
  const notification =
    accessData?.access?.find((module) => module.module === "Notifications") ||
    {};
  const can_edit = isAdmin || notification?.is_edit;
  // Expanded sections state
  const [expandedSections, setExpandedSections] = useState({
    "new-task": true,
    "task-deleted": false,
    "task-completed": false,
    "task-reassigned": false,
  });

  // Notification settings state
  const [notifications, setNotifications] = useState({
    // New task section
    "new-task": {
      browser: false,
      email: false,
      children: {
        "task-assignee": { browser: false, email: false },
        "task-creator": { browser: false, email: false },
      },
    },
    // Task deleted section
    "task-deleted": {
      browser: false,
      email: false,
      children: {
        "task-assignee": { browser: false, email: false },
        "task-creator": { browser: false, email: false },
      },
    },
    // Task completed section
    "task-completed": {
      browser: false,
      email: false,
      children: {
        "task-assignee": { browser: false, email: false },
        "task-creator": { browser: false, email: false },
      },
    },
    // Task reassigned section
    "task-reassigned": {
      browser: false,
      email: false,
      children: {
        "new-task-assignee": { browser: false, email: false },
        "task-creator": { browser: false, email: false },
      },
    },
  });

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // Handle parent checkbox changes
  const handleParentChange = (section, type, e) => {
    // Stop event propagation to prevent section toggle
    if (e) {
      e.stopPropagation();
    }

    const newState = { ...notifications };
    // Toggle the parent checkbox
    const newValue = !newState[section][type];
    newState[section][type] = newValue;

    // Set all children to the same value
    const childrenKeys = Object.keys(newState[section].children);
    childrenKeys.forEach((child) => {
      newState[section].children[child][type] = newValue;
    });

    setNotifications(newState);
  };

  // Handle child checkbox changes
  const handleChildChange = (section, childKey, type) => {
    const newState = { ...notifications };
    // Toggle the child checkbox
    newState[section].children[childKey][type] =
      !newState[section].children[childKey][type];

    // Check if all children are selected
    const allSelected = Object.keys(newState[section].children).every(
      (child) => newState[section].children[child][type]
    );

    // Update parent checkbox based on children state
    newState[section][type] = allSelected;

    setNotifications(newState);
  };

  // Check if parent is in indeterminate state (some but not all children selected)
  const isIndeterminate = (section, type) => {
    const children = notifications[section].children;
    const someSelected = Object.keys(children).some(
      (child) => children[child][type]
    );
    const allSelected = Object.keys(children).every(
      (child) => children[child][type]
    );

    return someSelected && !allSelected;
  };

  const Checkbox = ({ checked, onChange, indeterminate = false, disabled }) => (
    <div className="checkbox-wrapper" onClick={(e) => e.stopPropagation()}>
      <CheckboxWrapper
        type="checkbox"
        checked={checked}
        onChange={onChange}
        className={
          indeterminate
            ? "notification-checkbox indeterminate"
            : "notification-checkbox"
        }
        ref={(el) => {
          if (el) {
            el.indeterminate = indeterminate;
          }
        }}
        disabled={disabled}
      />
    </div>
  );

  const SectionHeader = ({ title, section, expanded, onClick }) => (
    <div className="section-header" onClick={onClick}>
      <span className="title">
        {title}{" "}
        {expanded ? (
          <ChevronUp size={20} className="cursor-pointer" />
        ) : (
          <ChevronDown size={20} className="cursor-pointer" />
        )}
      </span>

      <div className="checkboxes" onClick={(e) => e.stopPropagation()}>
        <div className="checkbox-wrapper">
          <Checkbox
            checked={notifications[section].browser}
            onChange={(e) => handleParentChange(section, "browser", e)}
            indeterminate={isIndeterminate(section, "browser")}
            disabled={!can_edit}
          />
        </div>
        <div className="checkbox-wrapper">
          <Checkbox
            checked={notifications[section].email}
            onChange={(e) => handleParentChange(section, "email", e)}
            indeterminate={isIndeterminate(section, "email")}
            disabled={!can_edit}
          />
        </div>
      </div>
    </div>
  );

  const NotificationRow = ({ label, section, childKey, indented = false }) => (
    <div className={`notification-row ${indented ? "indented" : ""}`}>
      <span className="label">{label}</span>
      <div className="checkboxes">
        <div className="checkbox-wrapper">
          <Checkbox
            checked={notifications[section].children[childKey].browser}
            onChange={() => handleChildChange(section, childKey, "browser")}
            disabled={!can_edit}
          />
        </div>
        <div className="checkbox-wrapper">
          <Checkbox
            checked={notifications[section].children[childKey].email}
            onChange={() => handleChildChange(section, childKey, "email")}
            disabled={!can_edit}
          />
        </div>
      </div>
    </div>
  );

  const getNotificationSettings = async () => {
    const res = await getData({
      endpoint: "NotificationSettings",
      token: cookies.e_t,
    });
  
    const setting = res?.data;
    setNotificationData(setting);
  
    // Deep copy the original notification structure
    const updatedNotifications = JSON.parse(JSON.stringify(notifications));
  
    // Step 1: Update each notification and its children based on API data
    setting.forEach((item) => {
      const { event_type, role, browser_alerts, email } = item;
  
      if (updatedNotifications[event_type]) {
        if (role && updatedNotifications[event_type].children?.[role]) {
          // Child level update
          updatedNotifications[event_type].children[role] = {
            browser: browser_alerts,
            email: email,
          };
        } else if (role === "") {
          // Parent level update
          updatedNotifications[event_type].browser = browser_alerts;
          updatedNotifications[event_type].email = email;
        }
      }
    });
  
    // Step 2: Update parent browser/email based on children status
    Object.keys(updatedNotifications).forEach((eventType) => {
      const event = updatedNotifications[eventType];
      const children = event.children;
  
      if (children) {
        const childrenArray = Object.values(children);
  
        // Check if all children have browser: true
        const allBrowsersTrue = childrenArray.every((child) => child.browser === true);
        event.browser = allBrowsersTrue;
  
        // Check if all children have email: true
        const allEmailsTrue = childrenArray.every((child) => child.email === true);
        event.email = allEmailsTrue;
      }
    });
  
    setNotifications(updatedNotifications);
  };
  
  

  useEffect(() => {
    getNotificationSettings();
  }, []);

  const saveChanges = async () => {
    const payload = [];

    Object.keys(notifications).forEach((eventTypeKey) => {
      const event = notifications[eventTypeKey];

      Object.keys(event.children).forEach((roleKey) => {
        const roleData = event.children[roleKey];

        payload.push({
          event_type: eventTypeKey,
          role: roleKey,
          browser_alerts: roleData.browser,
          email: roleData.email,
        });
      });
    });

    try {
      const res = await postData({
        endpoint: "NotificationSettings/upsert",
        data: payload,
        token: cookies.e_t,
      });

      toast.success("Notification settings updated successfully", {
        duration: 2000,
        style: confimationStyles,
      });
    } catch (err) {}
  };


  return (
    <>
      <div className="notification-permissions">
        <div className="header">
          <div className="header-cell main-header">Task notifications</div>
          <div className="header-cell">Browser alerts</div>
          <div className="header-cell">Email</div>
        </div>

        <div className="sections">
          {/* New Task Section */}
          <div className="section">
            <SectionHeader
              title="New task created"
              section="new-task"
              expanded={expandedSections["new-task"]}
              onClick={() => toggleSection("new-task")}
            />
            {expandedSections["new-task"] && (
              <div className="section-content">
                <NotificationRow
                  label="Task assignee"
                  section="new-task"
                  childKey="task-assignee"
                  indented
                />
                <NotificationRow
                  label="Task creator"
                  section="new-task"
                  childKey="task-creator"
                  indented
                />
              </div>
            )}
          </div>

          {/* Task Deleted Section */}
          <div className="section">
            <SectionHeader
              title="Task deleted"
              section="task-deleted"
              expanded={expandedSections["task-deleted"]}
              onClick={() => toggleSection("task-deleted")}
            />
            {expandedSections["task-deleted"] && (
              <div className="section-content">
                <NotificationRow
                  label="Task assignee"
                  section="task-deleted"
                  childKey="task-assignee"
                  indented
                />
                <NotificationRow
                  label="Task creator"
                  section="task-deleted"
                  childKey="task-creator"
                  indented
                />
              </div>
            )}
          </div>

          {/* Task Completed Section */}
          <div className="section">
            <SectionHeader
              title="Task completed"
              section="task-completed"
              expanded={expandedSections["task-completed"]}
              onClick={() => toggleSection("task-completed")}
            />
            {expandedSections["task-completed"] && (
              <div className="section-content">
                <NotificationRow
                  label="Task assignee"
                  section="task-completed"
                  childKey="task-assignee"
                  indented
                />
                <NotificationRow
                  label="Task creator"
                  section="task-completed"
                  childKey="task-creator"
                  indented
                />
              </div>
            )}
          </div>

          {/* Task Reassigned Section */}
          <div className="section">
            <SectionHeader
              title="Task reassigned"
              section="task-reassigned"
              expanded={expandedSections["task-reassigned"]}
              onClick={() => toggleSection("task-reassigned")}
            />
            {expandedSections["task-reassigned"] && (
              <div className="section-content">
                <NotificationRow
                  label="New task assignee"
                  section="task-reassigned"
                  childKey="new-task-assignee"
                  indented
                />
                <NotificationRow
                  label="Task creator"
                  section="task-reassigned"
                  childKey="task-creator"
                  indented
                />
              </div>
            )}
          </div>
        </div>
      </div>



     {can_edit ?  <div className="mt-3 ms-4">
        <CustomButton
          text="Save Changes"
          handleClick={saveChanges}
          iconRequired={false}
          type="btn-primary"
        />
      </div> : null }
    </>
  );
};

export default NotificationPermissions;
